// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.LeftContentFieldDetail.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
//
// </copyright>
// ----------------------------------------------------------------------

/* eslint-disable react/no-find-dom-node */

/**
 * Field Detail component
 */
import React, { Component } from 'react';
import classnames from 'classnames';
import { FORMAT_GROUP_CONFIG } from 'logic/data/Izenda.ReportDesignerFormatsConstants';
import connect from 'share/enhancers/Izenda.Connect';
import ScrollBox from 'share/components/controls/Izenda.ScrollBox';
import GroupPanel from 'share/components/controls/Izenda.GroupPanel';
import Filters from './Filter/Izenda.Filters';
import ReportHeader from './Izenda.ReportHeader';
import ReportBody from './ReportBody/Izenda.ReportBody';
import ReportFooter from './Izenda.ReportFooter';
import ReportNameEditor from 'modules/ReportDesigner/Izenda.ReportNameEditor';
import TitleDescription from './Izenda.ReportTitleAndDescription';
import PropertiesEditorManager from 'modules/Izenda.PropertiesEditorManager';
import leftContentActions from './Izenda.LeftContentActions';
import { invokeArgs, get, getOr } from 'lodash/fp';
import ReactDOM from 'react-dom';
import { observable, action } from 'mobx';
import Constants from 'logic/data/Izenda.ReportDesignerConstants';

@connect(leftContentActions)
class LeftContent extends Component {
  @observable containerHeight;
  @observable headerPanelState;
  @observable titleDescriptionPanelState;
  @observable reportBodyPanelState;

  constructor(props) {
    super(props);
    this.containerHeight = 0;
    this.sections = {};
    this.headerPanelState = true;
    this.titleDescriptionPanelState = true;
    this.reportBodyPanelState = true;
  }
  /**
   * Add editor actions
   */
  addEditorActions(): void {
    const store = this.props.store;
    const propsEditorId = PropertiesEditorManager.findEditorId([
      {
        name: 'fieldProps'
      },
      {
        name: 'reportPartProps'
      }
    ]);

    const scrollTo = (sectionName: string) => {
      const scrollBox = this.scrollBox;
      let node = this[sectionName];
      node = node && ReactDOM.findDOMNode(node);

      invokeArgs(
        'scrollTo',
        [
          {
            left: 0,
            top: get('offsetTop', node)
          }
        ],
        scrollBox
      );
    };

    const beforeShowEditor = (sectionName: string) => () => {
      scrollTo(sectionName);
    };

    const scrollToConfigFormatSection = (): void => {
      const selectedFormatGroup = get('model.selectedFormatGroup', store);

      switch (selectedFormatGroup) {
        case FORMAT_GROUP_CONFIG.HEADER:
          return scrollTo('reportHeaderSection');
        case FORMAT_GROUP_CONFIG.TITLE_DESCRIPTION:
          return scrollTo('reportTitleSection');
        case FORMAT_GROUP_CONFIG.FOOTER:
          return scrollTo('reportFooterSection');
        default:
        // do nothing
      }
    };

    PropertiesEditorManager.addHandlers(propsEditorId, 'beforeRender', {
      filterProps: beforeShowEditor('filtersSection'),
      fieldProps: beforeShowEditor('reportBodySection'),
      reportPartProps: beforeShowEditor('reportBodySection'),
      formatItemProps: scrollToConfigFormatSection
    });
  }

  componentDidMount() {
    this.addEditorActions();
    this.adjustContainerHeight();
  }

  componentWillReceiveProps(nextProps: any) {
    if (nextProps.height !== this.props.height) {
      this.adjustContainerHeight();
    }
  }

  @action
  adjustContainerHeight = (): void => {
    if (this.container) {
      const top = this.container.getBoundingClientRect().top;
      // Subtract footer and padding
      this.containerHeight = window.innerHeight - (top + this.props.bottom);
    }
  };

  setContainer = (ref: any): void => {
    this.container = ref;
  };

  updateScrollBoxRef = (ref: any): void => {
    this.scrollBox = ref;
  };

  updateFilterSectionRef = (ref: any): void => {
    this.filtersSection = ref;
  };

  updateReportBodySectionRef = (ref: any): void => {
    this.reportBodySection = ref;
  };

  setGridViewScrollBox = (ref: any): void => {
    if (ref) {
      this.gridViewScrollBox = ref.getGridViewScrollBox();
      this.updateStickyScroll();
    }
  };

  getOuterScrollTop = (): number => {
    return this.scrollBox ? this.scrollBox.getScrollTop() : 0;
  };

  @action
  afterToggledFilterPanel = (): void => {
    this.adjustContainerHeight();
  };

  @action
  updateStickyScroll = (): void => {
    if (this.scrollBox && this.gridViewScrollBox) {
      const shouldStickScroll = this.checkShouldStickScroll();
      shouldStickScroll
        ? this.gridViewScrollBox.stickHorizontalScroll()
        : this.gridViewScrollBox.unstickHorizontalScroll();
    }
  };

  /** Check should stick horizontal scroll */
  checkShouldStickScroll() {
    if (this.checkHasOuterScroll() && this.scrollBox && this.gridViewScrollBox) {
      const scrollTop = this.scrollBox.getScrollTop();
      const headerDOM = ReactDOM.findDOMNode(this.refs.reportHeaderSection);
      const titleDOM = ReactDOM.findDOMNode(this.refs.reportTitleSection);
      const bodyDOM = ReactDOM.findDOMNode(this.reportBodySection);
      const headerHeight = getOr(0, 'offsetHeight', headerDOM);
      const titleHeight = getOr(0, 'offsetHeight', titleDOM);
      const trackHeight = this.scrollBox.getTrackHeight();
      return (
        headerHeight + titleHeight + bodyDOM.clientHeight + trackHeight >= this.containerHeight + scrollTop &&
        this.gridViewScrollBox.isInsideOfViewport()
      );
    }
    return false;
  }

  @action
  afterToggledFormatPanel(sectionName: string, state: boolean): void {
    switch (sectionName) {
      case 'reportHeaderSection':
        this.headerPanelState = state;
        break;
      case 'reportTitleSection':
        this.titleDescriptionPanelState = state;
        break;
      case 'reportBodySection':
        this.reportBodyPanelState = state;
        break;
      default:
        break;
    }
  }

  showReportHeaderAndFooter = ({ pageName, store: { reportStore } }): boolean => {
    return pageName === Constants.PAGE_NAME.FORMATS || reportStore.isShowingHeaderFooter;
  };

  checkHasOuterScroll() {
    const { store } = this.props;
    return (
      (store.isVisibleHeaderFooter && this.showReportHeaderAndFooter(this.props)) || store.isVisibleTitleDescription
    );
  }

  render() {
    const props = this.props;
    const store = props.store;
    const reportStore = store.reportStore;
    const isFullScreen = get('reportBody.isFullScreen', store);
    const showReportHeaderAndFooter = this.showReportHeaderAndFooter(props);

    const renderFormatContainer = (
      Component: any,
      showComponent: boolean,
      sectionName: any,
      isLastSection: boolean
    ): Object => {
      if (!showComponent || isFullScreen) {
        return null;
      }
      const classes = classnames('report-format-panel', { u_noMarginBottom: isLastSection });
      return (
        <GroupPanel className={classes} ref={sectionName}>
          <Component afterToggled={this.afterToggledFormatPanel.bind(this, sectionName)} store={store} />
        </GroupPanel>
      );
    };

    const hasOuterScroll = this.checkHasOuterScroll();
    const isReportBodyLastSection =
      hasOuterScroll && (!showReportHeaderAndFooter || !store.isVisibleHeaderFooter) && store.isVisibleTitleDescription;
    const classes = classnames('izenda-ReportGroupPanel content-container', {
      'izenda-ReportGroupPanel-Fullsize': isFullScreen,
      u_noMarginBottom: isFullScreen || isReportBodyLastSection
    });

    const filterPanelClass = classnames('izenda-FilterGroupPanel u_noMarginAll', {
      u_none: isFullScreen
    });

    const reportContentContainerStyle = { height: this.containerHeight };
    const scrollBoxStyle = hasOuterScroll
      ? null
      : { height: this.containerHeight + (isFullScreen ? 0 : this.props.bottom) };

    const reportContent = (
      <div className="u_paddingLeftRight">
        {showReportHeaderAndFooter &&
          renderFormatContainer(ReportHeader, store.isVisibleHeaderFooter, 'reportHeaderSection')}
        {renderFormatContainer(TitleDescription, store.isVisibleTitleDescription, 'reportTitleSection')}
        <GroupPanel className={classes} ref={this.updateReportBodySectionRef}>
          <ReportBody
            afterToggled={this.afterToggledFormatPanel.bind(this, 'reportBodySection')}
            headerPanelState={this.headerPanelState}
            titleDescriptionPanelState={this.titleDescriptionPanelState}
            reportBodyPanelState={this.reportBodyPanelState}
            finishRender={this.setGridViewScrollBox}
            {...props}
            store={store}
            hasOuterScroll={hasOuterScroll}
            getOuterScrollTop={this.getOuterScrollTop}
          />
        </GroupPanel>
        {showReportHeaderAndFooter &&
          renderFormatContainer(ReportFooter, store.isVisibleHeaderFooter, 'reportFooterSection', true)}
      </div>
    );

    return (
      <div className="u_fullHeight u-posRelative">
        <div className="u_paddingLeftRight">
          <ReportNameEditor
            store={reportStore}
            name={reportStore.model.reportName}
            showTooltip={false}
            isPreviewRecord
            onChangePreviewRecord={props.updateResults}
          />

          <GroupPanel className={filterPanelClass} ref={this.updateFilterSectionRef}>
            <Filters
              afterMove={this.afterToggledFilterPanel}
              afterToggled={this.afterToggledFilterPanel}
              store={store}
              isFullScreen={isFullScreen}
              uiStore={props.uiStore}
              updateResultInfo={props.updateResultInfo}
            />
          </GroupPanel>
        </div>
        <div style={reportContentContainerStyle} ref={this.setContainer}>
          <ScrollBox
            containerStyle={scrollBoxStyle}
            onScroll={this.updateStickyScroll}
            onUpdate={this.updateStickyScroll}
            ref={this.updateScrollBoxRef}
          >
            {reportContent}
          </ScrollBox>
        </div>
      </div>
    );
  }
}

export default LeftContent;



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/Izenda.LeftContentFieldDetail.js